// @greenlight-spec: 10-security-compliance.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, ChevronRight, ShieldCheck } from 'lucide-react';

import { Aurora } from '@/components/greenlight/Aurora';
import { Logo } from '@/components/greenlight/Logo';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { LEGAL_LINKS } from '@/lib/legal';
import type { SsoProvider } from '@greenlight/shared';

/**
 * Brand mark for an SSO provider button. Literal brand artwork (like the
 * static files under public/brands/), so the marks keep their official
 * colors; decorative only — the button text carries the accessible name.
 */
function ProviderMark({ type }: { type: SsoProvider['type'] }): React.ReactElement {
  if (type === 'google') {
    return <img src="/brands/google.svg" alt="" className="size-[19px] shrink-0" />;
  }
  if (type === 'entra') {
    return (
      <svg viewBox="0 0 21 21" className="size-[19px] shrink-0" aria-hidden focusable="false">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
      </svg>
    );
  }
  if (type === 'okta') {
    // Okta: the circular "aura" ring mark.
    return (
      <svg viewBox="0 0 24 24" className="size-[19px] shrink-0" aria-hidden focusable="false">
        <path
          fill="#007DC1"
          d="M12 0C5.39 0 0 5.39 0 12s5.39 12 12 12 12-5.39 12-12S18.61 0 12 0zm0 18c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"
        />
      </svg>
    );
  }
  // Generic OIDC has no brand: a neutral key mark in the button's own color.
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-[19px] shrink-0 text-muted-foreground"
      aria-hidden
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      data-testid="provider-mark-oidc"
    >
      <circle cx="8" cy="15" r="4" />
      <path d="M10.85 12.15 19 4" />
      <path d="M18 5l2 2" />
      <path d="M15 8l2 2" />
    </svg>
  );
}

/** The OIDC initiator URL for one provider, carrying `return_to` through. */
function signInHref(provider: SsoProvider, returnTo: string | null): string {
  return returnTo
    ? `${provider.login_url}?return_to=${encodeURIComponent(returnTo)}`
    : provider.login_url;
}

/**
 * The branded sign-in surface (docs/10 §Login flow). Rendered unauthenticated,
 * outside the AppShell, at the SPA route `/login` — both browser-entry paths
 * (the gateway's content-negotiated 302 and the InstallGuard client bootstrap)
 * land here. It never redirects on load; the "Sign in" button is a full-page
 * navigation to the unchanged `GET /auth/login` OIDC initiator, which is what
 * keeps the page and the initiator loop-free.
 *
 * Buttons are mapped from `GET /auth/providers` (one per element), so the page
 * is chooser-ready for the post-MVP multi-IdP extension; an empty list means
 * the IdP isn't configured yet and points IT at the install wizard instead of
 * advertising a sign-in that would fail.
 */
export function LoginPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to');
  // Always refetch on mount (overriding the app-wide 30s staleTime): the IdP
  // can become configured between visits — e.g. right after the install
  // wizard's IdP step — and a cached empty list would show the wizard pointer
  // instead of the sign-in button.
  const { data, isLoading, isFetching, isError } = api.auth.listProviders.useQuery(
    ['auth-providers'],
    undefined,
    {
      queryKey: ['auth-providers'],
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );

  React.useEffect(() => {
    document.title = 'Sign in · Greenlight';
    return () => {
      document.title = 'Greenlight';
    };
  }, []);

  // A non-200 envelope resolves as `data` (only thrown/network failures set
  // isError), so it must read as a fetch failure — never as "no IdP
  // configured", which would point users at the install wizard for an outage.
  const failed = isError || (data !== undefined && data.status !== 200);
  const providers = data?.status === 200 ? data.body.providers : [];
  // The no-IdP pointer is only trustworthy from a *settled* fetch: during the
  // mount refetch a cached empty list keeps isLoading false, so gate the empty
  // state on isFetching too. Cached sign-in buttons stay visible meanwhile —
  // they are valid either way.
  const showSkeleton = isLoading || (isFetching && !failed && providers.length === 0);

  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-x-hidden bg-background px-5 py-12">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 motion-safe:duration-1000 motion-safe:animate-in motion-safe:fade-in-0"
      >
        <Aurora />
        <div className="absolute inset-0 bg-[linear-gradient(0deg,hsl(var(--background))_0%,hsl(var(--background)/0.7)_30%,transparent_65%)]" />
      </div>

      <div className="fixed right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      <main className="relative z-[1] flex w-full max-w-[408px] flex-col items-center">
        <div className="mb-7 flex flex-col items-center gap-2">
          <Logo className="h-7 text-foreground" />
          <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
            by Shift Engineering
          </div>
        </div>

        <div className="w-full rounded-gl-lg border border-border bg-card p-6 shadow-xl sm:p-8">
          <Eyebrow>Console access</Eyebrow>
          <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em]">
            Sign in to Greenlight
          </h1>
          <p className="mb-6 mt-2 text-sm leading-relaxed text-muted-foreground">
            Continue with your organization&apos;s identity provider to access the dashboard.
          </p>

          {showSkeleton ? (
            <div className="flex flex-col gap-2.5" data-testid="login-loading">
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
            </div>
          ) : failed ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>Couldn&apos;t load sign-in options</AlertTitle>
              <AlertDescription>
                Something went wrong while checking your organization&apos;s identity provider.
                Refresh the page to try again.
              </AlertDescription>
            </Alert>
          ) : providers.length === 0 ? (
            <div
              className="rounded-lg border border-border bg-muted/50 p-4 text-sm leading-relaxed text-muted-foreground"
              data-testid="login-no-idp"
            >
              No identity provider is configured yet. An IT administrator needs to complete the{' '}
              <Link
                to="/install"
                className="font-medium text-link underline-offset-4 hover:underline"
              >
                install wizard
              </Link>{' '}
              to set up single sign-on before anyone can sign in.
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2.5">
                {providers.map((provider) => (
                  <Button
                    key={provider.type}
                    asChild
                    variant="outline"
                    className="h-11 w-full justify-start gap-3 px-3.5 text-sm font-medium"
                  >
                    <a href={signInHref(provider, returnTo)}>
                      <ProviderMark type={provider.type} />
                      <span className="flex-1 text-left">Sign in with {provider.display_name}</span>
                      <ChevronRight className="text-muted-foreground" aria-hidden />
                    </a>
                  </Button>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-2 border-t border-border pt-5 font-mono text-[11px] leading-relaxed text-muted-foreground">
                <ShieldCheck className="size-3.5 shrink-0" aria-hidden />
                <span>Single sign-on is managed by your IT administrator.</span>
              </div>
            </>
          )}
        </div>

        {/* Sits outside the card's state branches so the legal links are
            present whether the page shows buttons, a skeleton, or an error. */}
        <div
          className="mt-6 flex items-center gap-2 font-mono text-[11px] text-muted-foreground"
          data-testid="login-legal-links"
        >
          {LEGAL_LINKS.map((link, index) => (
            <React.Fragment key={link.href}>
              {index > 0 && <span aria-hidden>·</span>}
              <a
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="underline-offset-4 transition-colors hover:text-foreground hover:underline"
              >
                {link.label}
              </a>
            </React.Fragment>
          ))}
        </div>
      </main>
    </div>
  );
}
